import {
  deserializeTaskItems,
  TaskItemsDeserialize,
} from '../../../screens/admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';

export const TASK_ITEM_PAGE_BREAK = 'VOLUNTEER_TaskItemPageBreakType';

export type TaskItemPage = {
  key: string;
  title: string | null;
  items: any[];
};

function getParentItemId(taskItems: TaskItemsDeserialize, itemId: string): string | undefined {
  const optionId = taskItems.byTriggerOption[itemId];
  return optionId ? taskItems.byTrigger[optionId]?.taskItemId : undefined;
}

/**
 * A revealed item follows its parent rather than its own order, because the
 * renderer looks children up inside the parent's page.
 */
function findParentPageIndex(
  taskItems: TaskItemsDeserialize,
  pageIndexByItemId: { [itemId: string]: number },
  itemId: string
): number {
  const seen: { [id: string]: boolean } = { [itemId]: true };
  let parentId = getParentItemId(taskItems, itemId);

  while (parentId && !seen[parentId]) {
    if (pageIndexByItemId[parentId] !== undefined) return pageIndexByItemId[parentId];
    seen[parentId] = true;
    parentId = getParentItemId(taskItems, parentId);
  }

  return 0;
}

/**
 * Task items can be hidden for a volunteer's programs. Removing them before
 * splitting means a page whose every item is hidden collapses away, rather than
 * rendering as a blank step.
 */
export function filterHiddenTaskItems(taskItems: any[], volunteerProgramIds?: string[]): any[] {
  if (!volunteerProgramIds || volunteerProgramIds.length === 0) return taskItems || [];

  return (taskItems || []).filter((item) => {
    const { hiddenForProgramIds } = item;
    if (!hiddenForProgramIds || hiddenForProgramIds.length === 0) return true;
    return !volunteerProgramIds.some((programId) => hiddenForProgramIds.includes(programId));
  });
}

export function splitTaskItemsIntoPages(taskItems: any[]): TaskItemPage[] {
  const items = taskItems || [];
  const deserialized = deserializeTaskItems(items as any);

  const pages: TaskItemPage[] = [{ key: 'page-1', title: null, items: [] }];
  const pageIndexByItemId: { [itemId: string]: number } = {};
  const revealedItems: any[] = [];

  items.forEach((item) => {
    if (item.__typename === TASK_ITEM_PAGE_BREAK) {
      pages.push({
        key: item.taskItemId,
        title: item.title ?? null,
        items: [],
      });
      return;
    }

    if (deserialized.byTriggerOption[item.taskItemId]) {
      revealedItems.push(item);
      return;
    }

    pageIndexByItemId[item.taskItemId] = pages.length - 1;
    pages[pages.length - 1].items.push(item);
  });

  revealedItems.forEach((item) => {
    const pageIndex = findParentPageIndex(deserialized, pageIndexByItemId, item.taskItemId);
    pageIndexByItemId[item.taskItemId] = pageIndex;
    pages[pageIndex].items.push(item);
  });

  const populatedPages = pages.filter((page) => page.items.length > 0);
  return populatedPages.length > 0 ? populatedPages : pages.slice(0, 1);
}

export function getPageFieldNames(page: TaskItemPage): string[] {
  return page.items.reduce((names: string[], item) => {
    if (item.__typename === 'VOLUNTEER_TaskItemFieldType') {
      return names.concat(item.field.taskFieldId);
    }
    if (item.__typename === 'VOLUNTEER_TaskItemRepeatableGroupType') {
      return names.concat(`__rg_${item.taskItemId}`);
    }
    return names;
  }, []);
}
